import { capitalizeFirstLetter } from '@eneco/common/string';
import { Label } from '@eneco/dc/client/types';
import { putServiceLocationNextBestActionFeedback } from '@eneco/dc/services';
import { getOfferCostDetailsDetails, mergeOfferCosts } from '@eneco/flows/helpers';
import {
  DC_Domain_Models_Products_CostDetailType,
  DC_Domain_Models_Products_ProductType,
  Products_Offers_ProductCostDetail,
  Products_VatModel,
  Products_Offers_V3_OfferProductCombinationResponseModel,
  Products_Offers_V3_OfferUsagesResponseModel,
  DC_Domain_Models_Products_DiscountType,
  DC_Repositories_Base_Enumerations_BusinessUnit,
} from '@monorepo-types/dc';

import { ENECO_ENERGY_FLOW_GIFT } from './constants';
import { FlowConsumerContextEnergy as FlowContext } from '../../context';
import { EnergyType, Product, Offer, Price, OfferTermsAndConditions, OfferContractSpan, OfferResponse } from '../types';
import { NbaStatus } from '../types/types';

// Shared pure helpers — keep exporting from this module for local callers/tests.
export { mergeOfferCosts, getOfferCostDetailsDetails };
export { getProductCategoryFromProductType } from '@eneco/flows/helpers';

export const getCrossSellRentalDeviceProductAvailability = (offer?: OfferResponse): boolean =>
  !!offer?.products?.some(p => p.productType === 'huurapparaat');

export const getUpsellProductAvailability = (
  productType: DC_Domain_Models_Products_ProductType,
  flowContext: FlowContext,
  offer?: OfferResponse,
): boolean => getUpsellProductPrice(productType, flowContext, offer) !== 0;

const getOfferContractSpanFromProducts = (products: Product[]): OfferContractSpan[] =>
  products.map(product => {
    return product?.contractSpan
      ? {
          ...product.contractSpan,
          // Threat "warmthEkv" and "bronwarmte" as regular "warmth" product
          type: product?.type === 'warmthEkv' || product?.type === 'bronwarmte' ? 'warmth' : product?.type,
          priceDifferentiation: product.priceDifferentiation,
        }
      : {
          duration: 0,
          byPeriod: 'D',
          type: 'unknown',
          priceDifferentiation: product.priceDifferentiation,
        };
  });

export const selectProductCombinationByDiscountCode = (
  productCombinations: Products_Offers_V3_OfferProductCombinationResponseModel[],
  discountCode?: string,
  discountType?: DC_Domain_Models_Products_DiscountType,
): Products_Offers_V3_OfferProductCombinationResponseModel | undefined =>
  productCombinations.find(c => c.discountCode === discountCode) ??
  productCombinations.find(c => c.discountType === discountType) ??
  productCombinations[0];

const selectCostDetailsByType = (
  costDetailsType: DC_Domain_Models_Products_CostDetailType,
  costDetails?: Products_Offers_ProductCostDetail[] | null,
): Products_VatModel | undefined => (costDetails ?? []).find(i => i.type === costDetailsType)?.costs;

export const getEnergyTypesFromProductTypes = (
  productCombinations: Array<Array<DC_Domain_Models_Products_ProductType>>,
): EnergyType[] => {
  const energyTypes: EnergyType[] = [];

  productCombinations?.forEach(productCombination => {
    // Threat "warmthEkv" as regular "warmth" product
    const warmthEkvIndex = productCombination.indexOf('warmthEkv');

    if (warmthEkvIndex !== -1) {
      productCombination[warmthEkvIndex] = 'warmth';
    }

    // Remove "warmtewisselaar" product since it is not an "energy" type product
    const warmtewisselaarIndex = productCombination.indexOf('warmtewisselaar');
    if (warmtewisselaarIndex !== -1) {
      productCombination.splice(warmtewisselaarIndex, 1);
    }

    // Product Combination Check: ELECTRICITY && GAS && SOMETHING
    if (productCombination.includes(EnergyType.Electricity) && productCombination.includes(EnergyType.Gas)) {
      // ELECTRICITY && GAS && WARMTH && TAPWATER && COOLING
      if (
        productCombination.includes(EnergyType.Warmth) &&
        productCombination.includes(EnergyType.TapWater) &&
        productCombination.includes(EnergyType.Cooling)
      ) {
        energyTypes.unshift(EnergyType.ElectricityAndGasAndWarmthAndTapWaterAndCooling); // the long problematic address
      }
      // ELECTRICITY && GAS && WARMTH && TAPWATER
      else if (productCombination.includes(EnergyType.Warmth) && productCombination.includes(EnergyType.TapWater)) {
        energyTypes.unshift(EnergyType.ElectricityAndGasAndWarmthAndTapWater);
      }
      // ELECTRICITY && GAS && WARMTH
      else if (productCombination.includes(EnergyType.Warmth)) {
        energyTypes.unshift(EnergyType.ElectricityAndGasAndWarmth);
      }
      // ELECTRICITY && GAS && TAPWATER
      else if (productCombination.includes(EnergyType.TapWater)) {
        energyTypes.unshift(EnergyType.ElectricityAndGasAndTapWater);
      }
      // ELECTRICITY && GAS
      else {
        energyTypes.unshift(EnergyType.ElectricityAndGas);
      }
    }
    // Product Combination Check: ELECTRICITY && WARMTH && SOMETHING
    else if (productCombination.includes(EnergyType.Electricity) && productCombination.includes(EnergyType.Warmth)) {
      // ELECTRICITY && WARMTH && TAPWATER && COOLING
      if (productCombination.includes(EnergyType.TapWater) && productCombination.includes(EnergyType.Cooling)) {
        energyTypes.unshift(EnergyType.ElectricityAndWarmthAndTapWaterAndCooling);
      }
      // ELECTRICITY && WARMTH && TAPWATER
      else if (productCombination.includes(EnergyType.TapWater)) {
        energyTypes.unshift(EnergyType.ElectricityAndWarmthAndTapWater);
      }
      // ELECTRICITY && WARMTH && COOLING
      else if (productCombination.includes(EnergyType.Cooling)) {
        energyTypes.unshift(EnergyType.ElectricityAndWarmthAndCooling);
      }
      // ELECTRICITY && WARMTH
      else {
        energyTypes.unshift(EnergyType.ElectricityAndWarmth);
      }
    }
    // Product Combination Check: ELECTRICITY && TAPWATER
    else if (productCombination.includes(EnergyType.Electricity) && productCombination.includes(EnergyType.TapWater)) {
      energyTypes.unshift(EnergyType.ElectricityAndTapWater);
    }
    // Procuct Combination Check: WARMTH && TAPWATER
    else if (productCombination.includes(EnergyType.Warmth) && productCombination.includes(EnergyType.TapWater)) {
      // WARMTH && TAPWATER && COOLING
      if (productCombination.includes(EnergyType.Cooling)) {
        energyTypes.unshift(EnergyType.WarmthAndTapWaterAndCooling);
      } else {
        // WARMTH && TAPWATER
        energyTypes.unshift(EnergyType.WarmthAndTapWater);
      }
    }
    // Procuct Combination Check: WARMTH && COOLING
    else if (productCombination.includes(EnergyType.Warmth) && productCombination.includes(EnergyType.Cooling)) {
      // WARMTH && COOLING
      energyTypes.unshift(EnergyType.WarmthAndCooling);
    }
    // Product Combination Check: ELECTRICITY && GEOTHERMALHEATING (bronwarmte)
    else if (productCombination.includes(EnergyType.Electricity) && productCombination.includes('bronwarmte')) {
      energyTypes.unshift(EnergyType.ElectricityAndGeothermalHeating);
    }
    // Single Product Choice Check:
    else if (productCombination.length === 1) {
      const capitalizedProductType = capitalizeFirstLetter(productCombination[0]);

      if (productCombination.includes('bronwarmte')) {
        energyTypes.unshift(EnergyType.GeothermalHeating);
      } else if (productCombination.includes('tapwater')) {
        energyTypes.unshift(EnergyType.TapWater);
      } else if (capitalizedProductType in EnergyType) {
        energyTypes.unshift(EnergyType[capitalizedProductType as keyof typeof EnergyType]);
      }
    }
  });

  return energyTypes;
};

// A contract extension only renews commodity contracts, so cashback/rental products that ride
// along in the offer must not influence the energy type.
const EXTENDABLE_PRODUCT_TYPES = new Set<DC_Domain_Models_Products_ProductType>(['electricity', 'gas']);

const selectExtendableProductTypes = (
  productTypes: Array<DC_Domain_Models_Products_ProductType | undefined>,
): Array<DC_Domain_Models_Products_ProductType> => [
  ...new Set(
    productTypes.filter(
      (productType): productType is DC_Domain_Models_Products_ProductType =>
        !!productType && EXTENDABLE_PRODUCT_TYPES.has(productType),
    ),
  ),
];

/**
 * The energy type a contract extension offer actually covers.
 *
 * The account's product types describe everything the customer has; an extension offer often
 * covers only part of that — a gas-only renewal for an electricity + gas customer. Deriving the
 * energy type from the offer keeps it in step with the products the offer step renders, notably
 * the Sitecore content key {@link constructTextualDataKey} builds from an offer's contract spans.
 *
 * Falls back to the account's product types when the offer carries no commodity products.
 */
export const getExtensionEnergyType = (
  offer?: OfferResponse,
  accountProductTypes?: Array<DC_Domain_Models_Products_ProductType> | null,
): EnergyType | undefined => {
  const offerProductTypes = selectExtendableProductTypes(
    (offer?.productCombinations ?? []).flatMap(
      productCombination => productCombination.products?.map(product => product.productType) ?? [],
    ),
  );

  const productTypes =
    offerProductTypes.length > 0 ? offerProductTypes : selectExtendableProductTypes(accountProductTypes ?? []);

  return getEnergyTypesFromProductTypes([productTypes])?.[0];
};

const toUsageValue = (value?: number | null): number | undefined => (value == null ? undefined : Number(value));

/**
 * The usage values a contract extension offer reports, shaped for the flow context.
 *
 * DC returns `null` (or omits the field) for a commodity the customer does not have, and a real
 * `0` for a commodity they have but did not use. `Number(null)` is `0`, so reading these straight
 * off the response made both cases look the same. Absent commodities land as `undefined` so the
 * consumption overview hides them, while a reported `0` is kept so it is shown. The personal
 * (extension) offer carries no warmth usages, so those stay `undefined` there.
 */
export const getExtensionUsageValues = (
  usages?: Products_Offers_V3_OfferUsagesResponseModel,
): Pick<
  FlowContext,
  | 'hasDoubleMeter'
  | 'hasSolarPanels'
  | 'usageElectricityHigh'
  | 'usageElectricityLow'
  | 'usageGas'
  | 'usageWarmth'
  | 'usageWater'
  | 'solarPanelsOutput'
> => {
  const usageElectricityHigh = toUsageValue(usages?.electricityDeliveryNormal);
  const usageElectricityLow = toUsageValue(usages?.electricityDeliveryLow);

  const redeliveryNormal = Number(usages?.electricityRedeliveryNormal) || 0;
  const redeliveryLow = Number(usages?.electricityRedeliveryLow) || 0;

  return {
    hasDoubleMeter: !!(usageElectricityHigh && usageElectricityLow),
    hasSolarPanels: !!(redeliveryNormal || redeliveryLow),

    usageElectricityHigh,
    usageElectricityLow,
    usageGas: toUsageValue(usages?.gas),
    usageWarmth: toUsageValue(usages?.warmthElectricity),
    usageWater: toUsageValue(usages?.warmthWater),

    solarPanelsOutput: redeliveryNormal + redeliveryLow || undefined,
  };
};

export const getProductTypesFromEnergyType = (
  energyType?: EnergyType,
): Array<DC_Domain_Models_Products_ProductType> => {
  switch (energyType) {
    case EnergyType.Electricity:
      return ['electricity'];
    case EnergyType.ElectricityAndGas:
      return ['electricity', 'gas'];
    case EnergyType.ElectricityAndTapWater:
      return ['electricity', 'tapwater', 'warmtewisselaar', 'warmth'];
    case EnergyType.ElectricityAndGasAndTapWater:
      return ['electricity', 'gas', 'tapwater', 'warmtewisselaar', 'warmth'];
    case EnergyType.TapWater:
      return ['warmth'];
    case EnergyType.ElectricityAndGasAndWarmth:
      return ['electricity', 'gas', 'warmth', 'warmthEkv', 'warmtewisselaar'];
    case EnergyType.ElectricityAndGasAndWarmthAndTapWater:
      return ['electricity', 'gas', 'warmth', 'warmthEkv', 'tapwater', 'warmtewisselaar'];
    case EnergyType.ElectricityAndGasAndWarmthAndTapWaterAndCooling:
      return ['electricity', 'gas', 'warmth', 'warmthEkv', 'tapwater', 'warmtewisselaar', 'cooling'];
    case EnergyType.ElectricityAndWarmth:
      return ['electricity', 'warmth', 'warmthEkv', 'warmtewisselaar'];
    case EnergyType.ElectricityAndWarmthAndCooling:
      return ['electricity', 'warmth', 'warmthEkv', 'warmtewisselaar', 'cooling'];
    case EnergyType.ElectricityAndWarmthAndTapWater:
      return ['electricity', 'warmth', 'warmthEkv', 'tapwater', 'warmtewisselaar'];
    case EnergyType.ElectricityAndWarmthAndTapWaterAndCooling:
      return ['electricity', 'warmth', 'warmthEkv', 'tapwater', 'warmtewisselaar', 'cooling'];
    case EnergyType.ElectricityAndGeothermalHeating:
      return ['electricity', 'bronwarmte', 'warmth'];
    case EnergyType.Gas:
      return ['gas'];
    case EnergyType.Cooling:
      return ['cooling'];
    case EnergyType.Warmth:
      return ['warmth', 'warmthEkv', 'warmtewisselaar'];
    case EnergyType.WarmthAndCooling:
      return ['warmth', 'warmthEkv', 'warmtewisselaar', 'cooling'];
    case EnergyType.WarmthAndTapWater:
      return ['warmth', 'warmthEkv', 'tapwater', 'warmtewisselaar'];
    case EnergyType.WarmthAndTapWaterAndCooling:
      return ['warmth', 'warmthEkv', 'tapwater', 'warmtewisselaar', 'cooling'];
    case EnergyType.GeothermalHeating:
      return ['bronwarmte', 'warmth'];
    default:
      return [];
  }
};

const getProducts = (
  selectedProductCombination?: Products_Offers_V3_OfferProductCombinationResponseModel,
  offer?: OfferResponse,
  flowContext?: FlowContext,
): Product[] => {
  return (selectedProductCombination?.products
    ?.filter(p => p.productType !== 'cashback')
    ?.filter(p => {
      // do not apply any filter for extension
      if (flowContext?.customerId) {
        return true;
      }
      // Filter out time of use contract if customer doesn't have smart meter
      const selectedProduct = offer?.products?.find(product => product.marketingProductCode === p.marketingProductCode);
      return selectedProduct?.priceDifferentiation !== 'TimeOfUse' || flowContext?.hasDoubleMeter;
    })
    ?.map(product => {
      const selectedProduct = offer?.products?.find(p => p.marketingProductCode === product.marketingProductCode);

      return (
        selectedProduct && {
          type: selectedProduct.productType,
          code: selectedProduct.marketingProductCode,

          productCombinationCode: selectedProductCombination?.discountCode ?? undefined,
          variant: selectedProductCombination?.discountCode ?? undefined,

          description: selectedProduct.description,

          contractSpan: selectedProduct.contractSpan,

          costsMonthly: selectCostDetailsByType('monthlyCosts', selectedProduct.costDetails),
          costsYearly: selectCostDetailsByType('yearlyCosts', selectedProduct.costDetails),

          costDetails: {
            [`${product.productType}CostDetails`]: selectedProduct.productRateDetails?.reduce(
              (acc, item) =>
                Object.assign(acc, {
                  [item.type]: {
                    price: {
                      vat: item.vat,
                      vatExcluded: item.vatExcluded,
                      vatIncluded: item.vatIncluded,
                    },
                    denotationType: item?.denotationType,
                    gridOperatorName: item?.gridOperatorName,
                    components: item.components,
                    ...(item.type === 'standingChargeSurcharge' && {
                      capacity:
                        selectedProduct.costDetails?.find(cd => cd.type === 'standingChargeSurcharge')?.capacity ?? '',
                    }),
                  },
                }),
              {},
            ),

            [`${product.productType}FutureCostDetails`]: selectedProduct.futureCostDetails?.reduce(
              (acc, item) =>
                Object.assign(acc, {
                  [item?.type ?? '']: {
                    price: {
                      vat: item?.costs?.vatAmount,
                      vatExcluded: item?.costs?.vatExcluded,
                      vatIncluded: item?.costs?.vatIncluded,
                    },
                    usage: item.usage,
                  },
                }),
              {},
            ),

            [`${product.productType}EnergyTaxDetails`]: (() => {
              interface EnergyTaxBracketCost {
                vatAmount?: number;
                vatExcluded?: number;
                vatIncluded?: number;
              }

              const costsByBracket = (selectedProduct.energyTaxDetails?.costs ?? []).reduce(
                (acc, item) =>
                  Object.assign(acc, {
                    [`bracketNr${item.bracketNr}`]: {
                      vatAmount: item.vatAmount,
                      vatExcluded: item.vatExcluded,
                      vatIncluded: item.vatIncluded,
                    },
                  }),
                {} as Record<string, EnergyTaxBracketCost>,
              );

              return selectedProduct.energyTaxDetails?.tariffs?.reduce(
                (acc, item) =>
                  Object.assign(acc, {
                    [`bracketNr${item.bracketNr}`]: {
                      active: item.usage > 0,
                      usageMax: item.bracketUsageMax,
                      usageMin: item.bracketUsageMin,
                      usage: item.usage,
                      price: {
                        vatAmount: item.vatAmount,
                        vatExcluded: item.vatExcluded,
                        vatIncluded: item.vatIncluded,
                      },
                      cost: costsByBracket[`bracketNr${item.bracketNr}`],
                    },
                  }),
                {},
              );
            })(),

            [`${product.productType}Total`]: selectedProduct.costDetails?.reduce(
              (acc, item) =>
                Object.assign(acc, {
                  [item.type ?? '']: { ...item.costs, netUsage: item.netUsage ?? 0, usage: item.usage ?? 0 },
                }),
              {},
            ),
            tariffCostDetails: selectedProduct.productRateDetails?.find(item => item.description === 'Normaaltarief'),
          },

          contractStartDate: offer?.contractStartDate ?? undefined,
          contractEndDate: selectedProduct.contractEndDate ?? undefined,
          priceDifferentiation: selectedProduct.priceDifferentiation,
        }
      );
    }) ?? []) as Product[];
};

const getCashbackValue = (
  selectedProductCombination?: Products_Offers_V3_OfferProductCombinationResponseModel,
  offer?: OfferResponse,
): Price | undefined => {
  const cashbackProductMarketingProductCode = selectedProductCombination?.products?.find(
    p => p.productType === 'cashback',
  )?.marketingProductCode;

  const cashbackProduct = offer?.products?.find(
    p => p.internalDcMarketingProductCode === cashbackProductMarketingProductCode,
  );

  const cashbackProductCostDetails = cashbackProduct?.costDetails?.find(
    c => c.type === 'cashBackDirect' || c.type === 'cashBackOnYearNote' || c.type === ENECO_ENERGY_FLOW_GIFT,
  );

  if (cashbackProductCostDetails?.type === ENECO_ENERGY_FLOW_GIFT) {
    return {
      value: (cashbackProductCostDetails?.value || cashbackProductCostDetails?.costs?.vatIncluded) ?? 0,
      type: ENECO_ENERGY_FLOW_GIFT,
    };
  }

  return {
    value: Number((cashbackProductCostDetails?.value || cashbackProductCostDetails?.costs?.vatIncluded) ?? 0),
    type: cashbackProductCostDetails?.type ?? 'unKnown',
  };
};

export const getUpsellProductPrice = (
  productType: DC_Domain_Models_Products_ProductType,
  flowContext: FlowContext,
  offer?: OfferResponse,
): number => {
  const { discountCode, offerType } = flowContext;

  const costsType = 'vatIncluded';

  if (offer?.productCombinations) {
    const selectedProductCombination = selectProductCombinationByDiscountCode(
      offer.productCombinations,
      discountCode,
      offerType,
    );
    const selectedProduct = selectedProductCombination?.products?.find(p => p.productType === productType);

    const selectedProductCostDetails = offer?.products?.find(
      p => p.marketingProductCode === selectedProduct?.marketingProductCode,
    )?.costDetails;
    const alternativeProductCostDetails = offer?.products?.find(
      p =>
        p.marketingProductCode === selectedProduct?.upsellMarketingProductCode ||
        p.marketingProductCode === selectedProduct?.downsellMarketingProductCode,
    )?.costDetails;

    const selectedProductPrice = selectCostDetailsByType('monthlyCosts', selectedProductCostDetails)?.[costsType] ?? 0;
    const alternativeProductPrice =
      selectCostDetailsByType('monthlyCosts', alternativeProductCostDetails)?.[costsType] ?? selectedProductPrice;

    return Math.abs(selectedProductPrice - alternativeProductPrice);
  }

  return 0;
};

export const getOffers = (flowContext: FlowContext, offer?: OfferResponse): Offer[] => {
  const { energyType, hasUpsellGas = false, hasUpsellElectricity = false } = flowContext;

  const selectedProducts = [...getProductTypesFromEnergyType(energyType), 'cashback'];

  return (
    (offer?.productCombinations
      ?.filter(o =>
        o.products?.every(product => {
          if (!selectedProducts.includes(product.productType)) {
            return false;
          }

          if (product.productType === 'electricity') {
            if (hasUpsellElectricity && !!product.upsellMarketingProductCode) {
              return false;
            }

            if (!hasUpsellElectricity && !!product.downsellMarketingProductCode) {
              return false;
            }
          }

          if (product.productType === 'gas') {
            if (hasUpsellGas && !!product.upsellMarketingProductCode) {
              return false;
            }

            if (!hasUpsellGas && !!product.downsellMarketingProductCode) {
              return false;
            }
          }

          return true;
        }),
      )
      .map(o => getOffer({ ...flowContext, discountCode: o.discountCode ?? '' }, offer)) as Offer[]) ?? []
  );
};

export const getOffer = (flowContext: FlowContext, offer?: OfferResponse): Offer | undefined => {
  const { discountCode, energyType, hasCrossSellRentalDevice, offerType } = flowContext;

  // The discountCode prop is optional. If it’s not provided,
  // selectProductCombinationByDiscountCode defaults to the first available option.
  // This ensures a valid product is selected, even in cases where only warmth is present.
  if (offer?.productCombinations && energyType) {
    const selectedProductCombination = selectProductCombinationByDiscountCode(
      offer.productCombinations,
      discountCode,
      offerType,
    );

    let selectedProductCombinationMonthlyCosts = selectCostDetailsByType(
      'monthlyCosts',
      selectedProductCombination?.costDetails,
    );
    let selectedProductCombinationYearlyCosts = selectCostDetailsByType(
      'yearlyCosts',
      selectedProductCombination?.costDetails,
    );
    let selectedProductCombinationYearlyPromotionCosts = selectCostDetailsByType(
      'yearlyCostsPromotion',
      selectedProductCombination?.costDetails,
    );
    const selectedProductCombinationMonthlyCostsWithCashback = selectCostDetailsByType(
      'monthlyCostsWithCashBack',
      selectedProductCombination?.costDetails,
    );
    const selectedProductCombinationYearlyCostsWithCashback = selectCostDetailsByType(
      'yearlyCostsWithCashBack',
      selectedProductCombination?.costDetails,
    );

    const selectedProductCombinationProducts = getProducts(selectedProductCombination, offer, flowContext);
    const selectedElectricityProduct = selectedProductCombinationProducts.find(
      product => product.type === 'electricity',
    )?.code;

    const electricityOffer = offer.products?.find(p => p.marketingProductCode === selectedElectricityProduct);

    const productFutureRate = electricityOffer?.productFutureRate;
    const estimatedUsages = electricityOffer?.estimatedUsages;

    const selectedProductCombinationCashbackProduct = selectedProductCombination?.products?.find(
      p => p.productType === 'cashback',
    );

    const selectedProductCombinationCashbackProductCosts =
      selectedProductCombinationCashbackProduct && getCashbackValue(selectedProductCombination, offer);

    if (hasCrossSellRentalDevice) {
      const selectedRentalDeviceProductCombination = offer.productCombinations.find(p =>
        p.products?.some(product => product.productType === 'huurapparaat'),
      );

      if (selectedRentalDeviceProductCombination) {
        const selectedRentalDeviceProduct = getProducts(
          selectedRentalDeviceProductCombination,
          offer,
          flowContext,
        )?.[0];

        selectedProductCombinationMonthlyCosts = mergeOfferCosts(
          selectedProductCombinationMonthlyCosts,
          selectedRentalDeviceProduct?.costsMonthly,
        );

        selectedProductCombinationYearlyCosts = mergeOfferCosts(
          selectedProductCombinationYearlyCosts,
          selectedRentalDeviceProduct?.costsYearly,
        );

        selectedProductCombinationYearlyPromotionCosts = mergeOfferCosts(
          selectedProductCombinationYearlyPromotionCosts,
          selectedRentalDeviceProduct?.costsYearly,
        );

        if (selectedRentalDeviceProduct) {
          selectedProductCombinationProducts.push(selectedRentalDeviceProduct);
        }
      }
    }

    const offerObject: Offer = {
      type: selectedProductCombination?.discountCode ?? undefined,
      products: selectedProductCombinationProducts,
      discountType: selectedProductCombination?.discountType ?? '',
      costsMonthly: selectedProductCombinationMonthlyCosts,
      costsYearly: selectedProductCombinationYearlyCosts,
      yearlyCostsVariable: selectedProductCombination?.costDetails?.find(c => c.type === 'yearlyCostsVariable')?.costs,
      yearlyCostsFixed: selectedProductCombination?.costDetails?.find(c => c.type === 'yearlyCostsFixed')?.costs,
      yearlyCostsWithCashBack: selectedProductCombinationYearlyCostsWithCashback,
      costsMonthlyWithCashback: selectedProductCombinationMonthlyCostsWithCashback,
      costDetails: {
        ...getOfferCostDetailsDetails(selectedProductCombinationProducts),
        cashback: selectedProductCombinationCashbackProductCosts,
      },
      costDiscounts: {
        cashback: selectedProductCombinationCashbackProductCosts,
      },
      nbaContext: selectedProductCombination?.nbaContext,
      contractSpan: getOfferContractSpanFromProducts(selectedProductCombinationProducts),
      contractStartDate: selectedProductCombinationProducts?.find(product => product?.contractStartDate)
        ?.contractStartDate,
      contractEndDate: selectedProductCombinationProducts?.find(product => product.contractEndDate)?.contractEndDate,
      usages: offer.usages,
      productFutureRate,
      estimatedUsages,
    };

    if (
      selectedProductCombinationYearlyCosts?.vatExcluded !== selectedProductCombinationYearlyPromotionCosts?.vatExcluded
    ) {
      offerObject.costsYearlyPromotion = selectedProductCombinationYearlyPromotionCosts;
    }

    return offerObject;
  }

  return undefined;
};

// this function will getOffer opposite of your current selection
// with >>> without upsell product and other way around
export const getOfferAlternative = (flowContext: FlowContext, offer?: OfferResponse): Offer | undefined => {
  const { discountCode, hasUpsellGas, hasUpsellElectricity, offerType } = flowContext;

  if (offer?.productCombinations && discountCode) {
    const selectedProducts: string[] =
      selectProductCombinationByDiscountCode(offer.productCombinations, discountCode, offerType)?.products?.map(
        product => {
          if (product.productType === 'electricity') {
            return (
              (hasUpsellElectricity ? product.upsellMarketingProductCode : product.downsellMarketingProductCode) ??
              product.marketingProductCode ??
              ''
            );
          } else if (product.productType === 'gas') {
            return (
              (hasUpsellGas ? product.upsellMarketingProductCode : product.downsellMarketingProductCode) ??
              product.marketingProductCode ??
              ''
            );
          } else {
            return product.marketingProductCode ?? '';
          }
        },
      ) ?? [];

    const selectDiscountCode =
      offer?.productCombinations?.find(
        o =>
          o.products?.length === selectedProducts.length &&
          o.products?.every(product => {
            return selectedProducts.some(p => p === product.marketingProductCode);
          }),
      )?.discountCode ?? undefined;

    return getOffer({ ...flowContext, discountCode: selectDiscountCode }, offer);
  }
};

export const getOfferTermsAndConditions = (
  flowContext: FlowContext,
  offer?: OfferResponse,
): OfferTermsAndConditions[] | undefined => {
  const { discountCode, energyType, hasCrossSellRentalDevice = false, offerType } = flowContext;
  const salesConditions: OfferTermsAndConditions[] = [];

  if (!offer?.productCombinations?.length || !discountCode || !energyType) {
    return salesConditions;
  }

  const selectedProductCombination = selectProductCombinationByDiscountCode(
    offer.productCombinations,
    discountCode,
    offerType,
  );

  salesConditions.push(...mapProductCombinationTermsToOfferTermsAndConditions(selectedProductCombination));

  // conditional cross-sell rental device logic that will add rental device product
  // related "terms and conditions" document urls to the offer's conditions
  if (hasCrossSellRentalDevice) {
    const selectedRentalDeviceProductCombination = offer.productCombinations.find(p =>
      p.products?.some(product => product.productType === 'huurapparaat'),
    );

    salesConditions.push(
      ...mapProductCombinationTermsToOfferTermsAndConditions(selectedRentalDeviceProductCombination),
    );
  }

  return salesConditions;
};

const mapProductCombinationTermsToOfferTermsAndConditions = (
  productCombination?: Products_Offers_V3_OfferProductCombinationResponseModel,
): OfferTermsAndConditions[] => {
  return productCombination?.terms?.map(({ text, url }) => ({ title: text ?? '', href: url ?? '' })) ?? [];
};

export const handleNbaFeedback = (
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit,
  label: Label,
  status: NbaStatus,
  serviceLocationId: string,
  contextId?: string,
  actionId?: number,
  treatmentVariationId?: number,
  servingPointId?: number,
) => {
  if (!status) return null;

  const requestBody = {
    data: {
      status,
      contextId,
      actionId,
      treatmentVariationId,
      servingPointId: servingPointId ? +servingPointId : undefined,
    },
  };

  putServiceLocationNextBestActionFeedback({
    businessUnit: businessUnit.toLocaleLowerCase() as DC_Repositories_Base_Enumerations_BusinessUnit,
    label: label,
    serviceLocationId: serviceLocationId,
    requestBody,
  });
};
